// ── ElevenLabs Conversational AI — live voice mode (added 2026-10-11) ──
// Runs alongside (not instead of) the Kara wake-word / chat TTS loop.
//
// Session: GET /api/elevenlabs/convai-session decides how to connect —
//   • mode "webrtc": server fetched a private conversation token with ELEVENLABS_API_KEY
//   • mode "public": no working key → public agent ID over WebSocket
// Persona: server picks the agent (ken72 → Kara, others → Lala; env ELEVENLABS_AGENT_KARA/LALA).
// Client tools come from voiceTools.js (full control, incl. trades/settings behind spoken
// confirmation). They only fire if the agent has matching client tools — run
// scripts/elevenlabs_setup_agents.mjs --apply after changing voiceTools.js.
// SDK v1 requires useConversation to sit inside a ConversationProvider.
import React, { useEffect, useRef, useState } from "react";
import { ConversationProvider, useConversation } from "@elevenlabs/react";
import { VOICE_TOOLS, fromVoiceArgs } from "./voiceTools.js";

const FALLBACK_AGENT_ID = "agent_9201m1r1ztbsfn0r3vkykf8xsa21";
const C = { green:"#00d4aa", purple:"#a855f7", red:"#ef4444", amber:"#f59e0b" };

const json = (o) => JSON.stringify(o);

// ── Generic DOM control for hands-free use (ui_list_controls / ui_click / ui_scroll) ──
// Anything needing money/destructive intent requires the agent to pass confirmed:true (spoken yes).
const RISKY_LABEL = /\b(buy|sell|trade|call|put|rise|fall|execute|place|activate|deploy|delete|remove|reset|restart|stop|kill|close|stake|withdraw|deposit|live|logout|log out|sign out)\b/i;
const CONTROL_SEL = 'button, [role="button"], [role="tab"], a[href], select, input[type="checkbox"], input[type="radio"], [data-tab], summary';
function controlLabel(el) {
  const t = (el.getAttribute("aria-label") || el.innerText || el.getAttribute("title") || el.value || "").replace(/\s+/g, " ").trim();
  return t.slice(0, 60);
}
function visibleControls() {
  const out = [];
  for (const el of document.querySelectorAll(CONTROL_SEL)) {
    if (el.closest(".kara-elevenlabs-live")) continue;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2 || el.disabled) continue;
    const cs = getComputedStyle(el);
    if (cs.visibility === "hidden" || cs.display === "none" || cs.opacity === "0") continue;
    const label = controlLabel(el);
    if (label) out.push({ el, label, kind: el.tagName === "SELECT" ? "dropdown" : el.type === "checkbox" ? `toggle(${el.checked ? "on" : "off"})` : el.getAttribute("role") === "tab" ? "tab" : "button" });
  }
  return out;
}
function runLocalUi(name, p = {}) {
  if (name === "ui_list_controls") {
    const f = String(p.filter || "").toLowerCase();
    const seen = new Set();
    const list = visibleControls().filter(c => (!f || c.label.toLowerCase().includes(f)) && !seen.has(c.label) && seen.add(c.label));
    return { ok: true, count: list.length, controls: list.slice(0, 80).map(c => `${c.label} [${c.kind}]`) };
  }
  if (name === "ui_scroll") {
    const box = [...document.querySelectorAll("main, [class*='content'], [class*='tab-body'], body")].find(e => e.scrollHeight > e.clientHeight + 20) || document.scrollingElement;
    const h = window.innerHeight * 0.8;
    const top = { up: box.scrollTop - h, down: box.scrollTop + h, top: 0, bottom: box.scrollHeight }[p.direction];
    if (top == null) return { ok: false, error: "direction must be up, down, top or bottom" };
    box.scrollTo({ top, behavior: "smooth" });
    return { ok: true, direction: p.direction };
  }
  if (name === "ui_click") {
    const want = String(p.label || "").replace(/\s+/g, " ").trim().toLowerCase();
    if (!want) return { ok: false, error: "label required" };
    const all = visibleControls();
    const c = all.find(x => x.label.toLowerCase() === want)
      || all.find(x => x.label.toLowerCase().replace(/[^a-z0-9 ]/g, "").trim() === want.replace(/[^a-z0-9 ]/g, "").trim())
      || all.find(x => x.label.toLowerCase().includes(want));
    if (!c) return { ok: false, error: `no visible control matching "${p.label}"`, hint: "call ui_list_controls, or ui_set_tab first", nearby: all.slice(0, 25).map(x => x.label) };
    if (RISKY_LABEL.test(c.label) && p.confirmed !== true && p.confirmed !== "true") {
      return { ok: false, needs_confirmation: true, label: c.label, say: `Read back "${c.label}" and ask the user to confirm; call again with confirmed: true only after a clear yes.` };
    }
    c.el.scrollIntoView?.({ block: "center" });
    c.el.click();
    return { ok: true, clicked: c.label, kind: c.kind };
  }
  return { ok: false, error: `unknown local ui tool ${name}` };
}

function LiveButton({ apiFetch, runUiAction, username, contextText, latestEvent, recentAlerts, onActiveChange, onLog }) {
  const startedAtRef = useRef(0);
  const conversation = useConversation({
    onConnect: () => { startedAtRef.current = Date.now(); onLog?.("🗣️ ElevenLabs live voice connected", "scan"); },
    onDisconnect: () => {
      onLog?.("🗣️ ElevenLabs live voice ended", "scan");
      const secs = startedAtRef.current ? (Date.now() - startedAtRef.current) / 1000 : 0;
      startedAtRef.current = 0;
      if (secs > 0) apiFetch("/api/elevenlabs/convai-usage", { method: "POST", headers: { "Content-Type": "application/json" }, body: json({ seconds: secs }) }).catch(() => {});
    },
    onError: (err) => onLog?.(`🗣️ ElevenLabs error: ${err?.message || err}`, "error"),
  });
  const { status, isSpeaking } = conversation;
  const active = status === "connected" || status === "connecting";
  const [persona, setPersona] = useState("");
  const [voiceHealth, setVoiceHealth] = useState(null); // /api/elevenlabs/status

  // Let the parent pause the browser wake-word listener while we hold the mic.
  const onActiveRef = useRef(onActiveChange);
  onActiveRef.current = onActiveChange;
  useEffect(() => { onActiveRef.current?.(active); }, [active]);

  // Push dashboard state once connected (non-interrupting).
  const sentRef = useRef(false);
  useEffect(() => {
    if (status !== "connected") { sentRef.current = false; return; }
    if (sentRef.current || !contextText) return;
    sentRef.current = true;
    try { conversation.sendContextualUpdate(contextText); } catch (_) {}
  }, [status, contextText, conversation]);

  // Live updates during the call: new signals / trade results from the alerts feed.
  const lastEventRef = useRef(latestEvent);
  useEffect(() => {
    if (latestEvent === lastEventRef.current) return;
    lastEventRef.current = latestEvent;
    if (status === "connected" && latestEvent) {
      try { conversation.sendContextualUpdate(`Live dashboard update: ${latestEvent}`); } catch (_) {}
    }
  }, [latestEvent, status, conversation]);

  // ElevenLabs health for the indicator dot (chat voice circuit breaker).
  useEffect(() => {
    let alive = true;
    const load = () => apiFetch("/api/elevenlabs/status", { silent: true })
      .then(r => r.ok ? r.json() : null).then(d => { if (alive && d) setVoiceHealth(d); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    return () => { alive = false; clearInterval(t); };
  }, [apiFetch]);

  // Client tools — every tool in voiceTools.js runs through POST /api/voice/action as this user.
  // Money-moving tools are refused server-side unless confirmed:true (agent must get a spoken yes).
  const alertsRef = useRef(recentAlerts);
  alertsRef.current = recentAlerts;
  const clientTools = {};
  const runUiRef = useRef(runUiAction);
  runUiRef.current = runUiAction;
  for (const t of VOICE_TOOLS) {
    if (t.ui) {
      // Dashboard control — runs in this browser (same runUiAction as the text chatbots), no server hop.
      clientTools[t.name] = async (params = {}) => {
        const t0 = Date.now();
        try {
          const result = t.local ? runLocalUi(t.name, params || {})
            : await Promise.race([
                runUiRef.current ? runUiRef.current(t.name, fromVoiceArgs(t.name, params)) : Promise.reject(new Error("dashboard UI bridge not ready")),
                new Promise((_, rej) => setTimeout(() => rej(new Error("UI action timed out")), 20000)),
              ]);
          const ok = result?.ok !== false;
          onLog?.(`🗣️ ui ${t.name} → ${ok ? "ok" : "✖ " + (result?.error || (result?.needs_confirmation ? "needs confirmation" : "failed"))} (${Date.now() - t0}ms)`, ok ? "scan" : "error");
          return json(t.local ? result : { ok: true, result });
        } catch (e) {
          onLog?.(`🗣️ ui ${t.name} → ✖ ${e?.message || e}`, "error");
          return json({ ok: false, error: String(e?.message || e).slice(0, 300) });
        }
      };
      continue;
    }
    clientTools[t.name] = t.name === "get_recent_alerts"
      ? async () => json((alertsRef.current || []).slice(-10))
      : async (params = {}) => {
          const t0 = Date.now();
          try {
            const r = await apiFetch("/api/voice/action", { method: "POST", silent: true,
              headers: { "Content-Type": "application/json" }, body: json({ action: t.name, args: params || {} }) });
            const d = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
            onLog?.(`🗣️ tool ${t.name} → ${d.ok === false ? "✖ " + (d.error || (d.result?.needs_confirmation ? "needs confirmation" : "failed")) : "ok"} (${Date.now() - t0}ms)`, d.ok === false ? "error" : "scan");
            return json(d);
          } catch (e) {
            return json({ ok: false, error: `tool call failed: ${e?.message || e}` });
          }
        };
  }

  // Debug/e2e hook (mirrors window.__karaDebug): lets tests invoke the exact voice tool handlers.
  if (typeof window !== "undefined") window.__karaVoiceDebug = { clientTools, tools: VOICE_TOOLS.map(t => t.name) };

  const toggle = async () => {
    if (active) { conversation.endSession(); return; }
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      onLog?.(`🗣️ Mic access denied: ${e?.message || e}`, "error");
      return;
    }
    let session = null;
    try {
      const r = await apiFetch("/api/elevenlabs/convai-session", { silent: true });
      if (r.ok) session = await r.json();
    } catch (_) {}
    setPersona(session?.persona || "");
    const opts = session?.mode === "webrtc" && session.token
      ? { conversationToken: session.token, connectionType: "webrtc" }
      : { agentId: session?.agentId || FALLBACK_AGENT_ID, connectionType: "websocket" };
    conversation.startSession({ ...opts, clientTools });
  };

  const circuitOpen = voiceHealth?.circuit?.state === "OPEN";
  const ring = status === "connecting" ? C.amber : status === "connected" ? (isSpeaking ? C.purple : C.green) : status === "error" ? C.red : C.purple;
  const who = persona ? persona[0].toUpperCase() + persona.slice(1) : "Kara";
  const title = (status === "connected" ? (isSpeaking ? `${who} speaking — click to end` : "Listening — click to end")
              : status === "connecting" ? "Connecting…" : "Live voice call (ElevenLabs)")
              + (circuitOpen ? `\n⚠️ Chat voice: ElevenLabs off (${voiceHealth.circuit.reason}) — using Piper` : "");

  return (
    <div className="kara-elevenlabs-live" onClick={toggle} title={title} aria-label={title} data-state={status}
      style={{position:"fixed",bottom:72,right:16,zIndex:8000,width:46,height:46,borderRadius:"50%",
        background:active?"#140a28":`linear-gradient(135deg,${C.purple},#6366f1)`,border:`2px solid ${ring}`,
        display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:20,
        boxShadow:`0 3px 16px ${ring}66`,transition:"all 0.25s",
        animation:status==="connecting"?"pulse 0.9s infinite":isSpeaking?"pulse 1.2s infinite":"none"}}>
      {active ? (status === "connecting" ? "⏳" : isSpeaking ? "🔊" : "🎙️") : "🗣️"}
      {circuitOpen && <span style={{position:"absolute",top:0,right:0,width:10,height:10,borderRadius:"50%",background:C.red,border:"2px solid #070d1a"}}/>}
    </div>
  );
}

export default function ElevenLabsLiveWidget(props) {
  return (
    <ConversationProvider>
      <LiveButton {...props} />
    </ConversationProvider>
  );
}
